/**
 * Checks if the provided object complies with the Storage API.
 * @param {Storage} obj - The object to check for compliance.
 * @returns {boolean} True if the object complies with the Storage API, false otherwise.
 */
export function checkStorageApiCompliance(obj: Storage): boolean {
  return (
    typeof obj.setItem === 'function' &&
    typeof obj.getItem === 'function' &&
    typeof obj.removeItem === 'function' &&
    typeof obj.clear === 'function' &&
    typeof obj.key === 'function' &&
    typeof obj.length === 'number'
  )
}

/**
 * Checks if the provided storage is available and functional.
 * This is done by attempting to set and remove a test key.
 * @param {Storage} storage - The storage object to check.
 * @returns {boolean} True if the storage is available, false otherwise.
 */
export function isStorageAvailable(storage: Storage): boolean {
  try {
    const testKey = '__test__'
    storage.setItem(testKey, 'test')
    storage.removeItem(testKey)
    return true
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return false
  }
}

function findCookieRow(cookieName: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${cookieName}=`))
}

export function getCookieClient(cookieName: string): string | null {
  if (typeof document === 'undefined') return null
  const row = findCookieRow(cookieName)
  if (!row) return null
  const value = row.slice(cookieName.length + 1)
  // useCookie writes values URI encoded, so read them back the same way. A
  // value something else wrote with a stray % is returned as it is.
  try {
    return decodeURIComponent(value) || null
  } catch {
    return value || null
  }
}

/**
 * Writes a cookie from code with no Nuxt context, where useCookie can't run.
 * URI encoded on path /, like useCookie, so getCookieClient reads it back.
 */
export function setCookieClient(
  cookieName: string,
  value: string,
  maxAge: number,
  { secure = false } = {}
): void {
  if (typeof document === 'undefined') return
  const secureAttribute = secure ? '; Secure' : ''
  document.cookie = `${cookieName}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax${secureAttribute}`
}

/**
 * Rewrites an existing cookie unchanged with a fresh max-age, so its expiry
 * counts from this visit rather than the one that first set it. useCookie
 * only writes when the value changes, so without this a visitor who comes
 * back every day still loses the cookie once its max-age runs out. Written
 * with the same path useCookie uses, so it replaces the cookie in place.
 */
export function refreshCookieExpiry(cookieName: string, maxAge: number): void {
  if (typeof document === 'undefined') return
  const row = findCookieRow(cookieName)
  if (!row) return
  document.cookie = `${row}; path=/; max-age=${maxAge}`
}
